import { useMutation, useQuery } from '@apollo/client';
import { useCallback, useEffect, useMemo, useState } from 'react';

import type { BaseChange, Notification, ResponseError } from '../../../apis/graphql/types';
import { ACKNOWLEDGE_NOTIFICATIONS } from '../../../pages/Trafficking/TraffickingPage/cells/OmsAlertCell/hooks/queries/acknowledgeNotifications';
import {
  DEFAULT_AMOUNT_OF_NOTIFICATIONS_LOGS_TO_SHOW,
  LIMIT_AUDITS,
  NOTIFY_ON_NETWORK_STATUS_CHANGE,
  ROWS_TO_IGNORE,
} from '../../ChangeLog/constants';
import { openErrorToastAlert, openToastAlert } from '../../ToastAlert/toastAlert';
import type {
  ChangeLogNotificationEntry,
  NotificationEdge,
  UseNotificationsChangeLogProps,
  UseNotificationsChangeLogResult,
} from '../types';
import { usePagination } from './usePagination';
import { useNotificationsChangeLogList } from './utils';

type CategorizedState<T> = {
  campaigns: T[];
  lines: T[];
  orders: T[];
};

const getEventCounts = (
  edges: NotificationEdge[] | undefined
): NonNullable<UseNotificationsChangeLogResult['eventCounts']> => {
  const counts = { campaigns: 0, lines: 0, orders: 0 };
  if (!edges) {
    return counts;
  }

  const allEvents = edges.flatMap((edge) => edge?.node?.events ?? []);

  for (const event of allEvents) {
    if (!event?.changeList || event.changeList.length === 0) {
      continue;
    }

    const hasMeaningfulChanges = event.changeList.some(
      (change: BaseChange) => !ROWS_TO_IGNORE.includes(change.fieldName)
    );

    if (hasMeaningfulChanges) {
      switch (event.object.__typename) {
        case 'CampaignV5':
          counts.campaigns += 1;
          break;
        case 'AuditLineItemV5':
          counts.lines += 1;
          break;
        case 'OrderV5':
          counts.orders += 1;
          break;
      }
    }
  }

  return counts;
};

const createAcknowledgementPayload = (
  categorizedLogEntries: CategorizedState<NotificationEdge>,
  checkedIds: CategorizedState<string>
): { notificationId: string; objectIds: string[] }[] => {
  const groupedByNotification = new Map<string, string[]>();

  Object.keys(categorizedLogEntries).forEach((tabKey) => {
    const key = tabKey as keyof typeof categorizedLogEntries;
    const entriesForTab = categorizedLogEntries[key];
    const checkedForTab = checkedIds[key];

    if (checkedForTab?.length > 0) {
      entriesForTab.forEach((notificationEdge) => {
        notificationEdge.node.events.forEach((event) => {
          if (checkedForTab.includes(event.id)) {
            const parentNotificationId = notificationEdge.node.id;
            if (!groupedByNotification.has(parentNotificationId)) {
              groupedByNotification.set(parentNotificationId, []);
            }
            groupedByNotification.get(parentNotificationId)!.push(event.id);
          }
        });
      });
    }
  });

  return Array.from(groupedByNotification.entries()).map(([notificationId, objectIds]) => ({
    notificationId,
    objectIds,
  }));
};

export default function useNotificationsChangeLog({
  GET_NOTIFICATIONS,
  variables,
}: UseNotificationsChangeLogProps): UseNotificationsChangeLogResult {
  const [loadingFilter, setLoadingFilter] = useState(false);
  const [toggles, setToggles] = useState<CategorizedState<boolean>>({ campaigns: [], lines: [], orders: [] });
  const [checkedIds, setCheckedIds] = useState<CategorizedState<string>>({ campaigns: [], lines: [], orders: [] });
  const [desiredItemsToShow, setDesiredItemsToShow] = useState(DEFAULT_AMOUNT_OF_NOTIFICATIONS_LOGS_TO_SHOW);
  const [queryVariables, setQueryVariables] = useState({
    ...variables,
    paginationOptions: {
      limit: LIMIT_AUDITS,
      offset: 0,
    },
  });

  const { data, loading } = useQuery(GET_NOTIFICATIONS, {
    variables: queryVariables,
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: NOTIFY_ON_NETWORK_STATUS_CHANGE,
    onCompleted: () => {
      setLoadingFilter(false);
    },
  });

  const eventCounts = useMemo(() => getEventCounts(data?.getNotifications?.edges), [data]);

  const categorizedLogEntries = useMemo((): CategorizedState<NotificationEdge> => {
    const initialCategories: CategorizedState<NotificationEdge> = { campaigns: [], lines: [], orders: [] };
    if (!data?.getNotifications?.edges) return initialCategories;

    data.getNotifications.edges.forEach((edge: NotificationEdge) => {
      if (!edge?.node?.events) return;
      const { node } = edge;

      const orderEvents: ChangeLogNotificationEntry[] = [];
      const campaignEvents: ChangeLogNotificationEntry[] = [];
      const lineEvents: ChangeLogNotificationEntry[] = [];

      node.events.forEach((event) => {
        switch (event.object.__typename) {
          case 'OrderV5':
            orderEvents.push(event);
            break;
          case 'CampaignV5':
            campaignEvents.push(event);
            break;
          case 'AuditLineItemV5':
            lineEvents.push(event);
            break;
        }
      });
      if (orderEvents.length > 0) {
        initialCategories.orders.push({ ...edge, node: { ...node, events: orderEvents } });
      }
      if (campaignEvents.length > 0) {
        initialCategories.campaigns.push({ ...edge, node: { ...node, events: campaignEvents } });
      }
      if (lineEvents.length > 0) {
        initialCategories.lines.push({ ...edge, node: { ...node, events: lineEvents } });
      }
    });
    return initialCategories;
  }, [data]);

  useEffect(() => {
    setToggles({
      campaigns: categorizedLogEntries.campaigns.flatMap((edge) => edge.node.events).map(() => true),
      lines: categorizedLogEntries.lines.flatMap((edge) => edge.node.events).map(() => true),
      orders: categorizedLogEntries.orders.flatMap((edge) => edge.node.events).map(() => true),
    });
  }, [categorizedLogEntries]);

  const total = data?.getNotifications?.total ?? 0;
  const hasNextPage = data?.getNotifications?.pageInfo?.hasNextPage ?? false;

  const activeTab = variables.selectedTab as keyof CategorizedState<any>;
  const allEvents = useMemo(() => (categorizedLogEntries[activeTab] || []).flatMap((entry) => entry.node.events), [
    categorizedLogEntries,
    activeTab,
  ]);
  const { filteredChangeLogList } = useNotificationsChangeLogList({ allEvents });

  const toggleAt = useCallback(
    (index: number) => {
      setToggles((prev) => ({
        ...prev,
        [activeTab]: prev[activeTab].map((t, i) => (i === index ? !t : t)),
      }));
    },
    [activeTab]
  );

  const toggleAll = useCallback(
    (isOpen: boolean | boolean[]) => {
      setToggles((prev) => ({
        ...prev,
        [activeTab]: prev[activeTab].map(() => isOpen),
      }));
    },
    [activeTab]
  );

  const toggleCheckedAt = useCallback(
    (id: string, isChecked: boolean) => {
      setCheckedIds((prev) => {
        const currentIds = prev[activeTab];
        const newIds = isChecked ? [...currentIds, id] : currentIds.filter((checkedId) => checkedId !== id);
        return { ...prev, [activeTab]: newIds };
      });
    },
    [activeTab]
  );

  const [acknowledgeNotifications, { loading: isSubmitting }] = useMutation(ACKNOWLEDGE_NOTIFICATIONS, {
    refetchQueries: ['GetTrafficking'],
  });

  const submitAcknowledgements = useCallback(
    async (options: { onSuccess: () => void }) => {
      const payload = createAcknowledgementPayload(categorizedLogEntries, checkedIds);

      if (payload.length === 0) {
        openErrorToastAlert('Select at least one change to acknowledge', 'No Changes Selected');
        return;
      }

      try {
        const { data } = await acknowledgeNotifications({ variables: { input: payload } });

        const apiErrors = data?.acknowledgeNotifications?.errors ?? [];
        const successfulNotifications = data?.acknowledgeNotifications?.notifications ?? [];
        const errorMessage = apiErrors.map((e: ResponseError) => e.message).join(', ');

        if (successfulNotifications.length === 0 && apiErrors.length > 0) {
          openErrorToastAlert(errorMessage, 'Acknowledgement Failed');
        } else if (apiErrors.length > 0) {
          openErrorToastAlert(errorMessage, 'Partial Failure');
        } else if (successfulNotifications.length > 0) {
          const totalAcknowledgedCount = successfulNotifications.reduce(
            (count: number, notification: Notification) => count + (notification.events?.length || 0),
            0
          );
          const changeWord = totalAcknowledgedCount === 1 ? 'Change' : 'Changes';
          openToastAlert({
            alertType: 'success',
            message: `${totalAcknowledgedCount} ${changeWord} from OMS were acknowledged.`,
          });
          options.onSuccess();
        }
      } catch (e) {
        openErrorToastAlert(e.message, 'Failed to Acknowledge Changes');
      }
    },
    [categorizedLogEntries, checkedIds, acknowledgeNotifications]
  );

  const { filteredLogsToShow, hasMore, onNext } = usePagination({
    filteredChangeLogList,
    setQueryVariables,
    total,
    desiredItemsToShow,
    setDesiredItemsToShow,
    hasNextPage,
  });

  const toggleCheckedAll = useCallback(
    (isChecked: boolean) => {
      const allIds = filteredLogsToShow.map((entry) => entry.id);
      setCheckedIds((prev) => ({
        ...prev,
        [activeTab]: isChecked ? allIds : [],
      }));
    },
    [filteredLogsToShow, activeTab]
  );
  return {
    loading,
    loadingFilter,
    toggles: toggles[activeTab] || [],
    toggleAt,
    toggleAll,
    filteredLogsToShow,
    hasMore,
    onNext,
    toggleCheckedAt,
    toggleCheckedAll,
    checkedIds: checkedIds[activeTab] || [],
    eventCounts,
    submitAcknowledgements,
    isSubmitting,
  };
}
